import { Data as ArticleData, ContentFragment } from '@root/modules/article/types/article';
import { Data as AuthorData } from '@root/modules/author/types/author';
import { WithContext, NewsArticle, ImageObject } from 'schema-dts';
import { Config } from '@root/modules/channel/types/channel';
import { articleUrl, articleCommentsUrl } from '@root/common/filters/urlBuilders';
import { convertToLocal } from '@root/common/filters/dateFormater';
import { stripTags, truncate } from '@root/common/filters/htmlFormater';
import { getCustomCropsPerRatio, getFocuspointQueryString } from '@root/libs/api-provider/utils/pictureCustomCrops';
import { getEnv } from '@root/common/utils/getEnv';
import lowerCase from 'lodash/lowerCase';

type AuthorObject = {
  '@type': any;
  name: string;
  url?: string;
};

interface Metadata {
  title: string | null;
  credit: string | null;
  alignment: string | null;
  authors: string[];
}

interface PictureFragment extends ContentFragment {
  attrs: ContentFragment['attrs'] & {
    id: string;
    cropperData: Record<string, any>;
    metadata: Metadata;
  };
}

function getAuthors(article: ArticleData, baseUrl: string) {
  const authors: AuthorObject[] = [];

  article.authors.items.forEach((author: AuthorData) => {
    const authorObject: AuthorObject = { '@type': 'Person', name: author.name };

    const hasAuthorPage = author.attributes?.find((item) => item.key === 'authorPage')?.value === true;
    if (hasAuthorPage) {
      authorObject.url = `${baseUrl}/autorius/${author.slug}`;
    }

    authors.push(authorObject);
  });

  return authors;
}

function getImages(article: ArticleData) {
  const images: ImageObject[] = [];

  // Add metaImage
  if (article.metaImage) {
    const picture = article.metaImage;
    const customCrops = picture?.attributes?.cropperData?.customCrops;
    const customCropsPerRatio = getCustomCropsPerRatio(customCrops);
    const focuspoint = picture?.attributes?.cropperData?.focuspoint;
    const focusPointString = getFocuspointQueryString(focuspoint, article.id);
    const url = getEnv('mediaApiCropper');

    images.push({
      '@type': 'ImageObject',
      contentUrl: `${url}/${picture.id}.jpg?w=1200&h=800${focusPointString}${customCropsPerRatio['16:9']}`,
      caption: picture.attributes?.metadata?.title || null,
      creditText: picture.attributes?.metadata?.credit || null,
    });
  }

  // Add body images
  const bodyPictures = article.content.body.content.filter((item) => item.type === 'media' && lowerCase(item.attrs.type as string) === 'image');
  if (bodyPictures.length) {
    for (const item of bodyPictures) {
      const picture = item as PictureFragment;
      const customCrops = picture?.attrs?.cropperData?.customCrops;
      const customCropsPerRatio = getCustomCropsPerRatio(customCrops);
      const focuspoint = picture?.attrs?.cropperData?.focuspoint;
      const focusPointString = getFocuspointQueryString(focuspoint, article.id);
      const url = getEnv('mediaApiCropper');

      images.push({
        '@type': 'ImageObject',
        contentUrl: `${url}/${picture.attrs?.id}.jpg?w=1200&h=800${focusPointString}${customCropsPerRatio['16:9']}`,
        caption: picture.attrs?.metadata?.title || '',
        creditText: picture.attrs?.metadata?.credit || '',
        author: picture.attrs?.metadata?.authors?.map((author) => {
          return { '@type': 'Person', name: author };
        }),
      });
    }
  }

  return images;
}

function articleJsonld(baseMeta: Config['meta']['base'], article: ArticleData) {
  let url, commentsUrl: string | undefined;

  try {
    url = new URL(articleUrl({ article }), baseMeta.baseUrl).href;
  } catch {
    url = `${baseMeta.baseUrl}${articleUrl({ article })}`;
  }

  try {
    commentsUrl = new URL(articleCommentsUrl({ article }), baseMeta.baseUrl).href;
  } catch {
    commentsUrl = `${baseMeta.baseUrl}${articleCommentsUrl({ article })}`;
  }

  const articleLd: WithContext<NewsArticle> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: article.seoFields.seoTitle ? truncate(article.seoFields.seoTitle, 100) : truncate(stripTags(article.content.title.html), 100),
    description: article.seoFields.seoDescription
      ? stripTags(article.seoFields.seoDescription)
      : article.content.lead?.html
        ? stripTags(article.content.lead.html)
        : '',
    articleSection: article.primaryCategory?.name || '',
    commentCount: article.statistics.comments,
    discussionUrl: commentsUrl,
    datePublished: convertToLocal(article.publishAt),
    dateModified: convertToLocal(article.updatedAt),
    keywords: article.tags?.items.map((tag) => tag.name) || '',
    author: getAuthors(article, baseMeta.baseUrl),
    publisher: {
      '@type': 'Organization',
      name: baseMeta.title,
      logo: {
        '@type': 'ImageObject',
        url: baseMeta.imageData.src,
      },
    },
    image: getImages(article),
    isAccessibleForFree: !article.content.paywall.enabled,
    // Done according to Google documentation, but somewhy conflicts with schema-dts
    // Confidential Docs, access needed - https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?feed=client-side#include-structured-data-markup
    // @ts-ignore
    isPartOf: {
      '@type': ['CreativeWork', 'Product'],
      name: baseMeta.title,
      productID: 'delfi.lt:showcase',
    },
  };

  return articleLd;
}

export default articleJsonld;
